/**
 * The client half of the imagery cost guards.
 *
 * Every one of these is money, not polish (docs/MAP_IMAGERY_RESEARCH.md §3). A request the
 * browser never sends is the cheapest guard there is; the server's own guards — the bbox
 * area limit and the 6 h / 2 h caches — are in backend/app/api/satellite.py and hold even
 * when a client misbehaves.
 *
 * Ported from the main project's `useSatelliteTimeline`, which has run these four numbers
 * in production, rather than reinvented. What is added here is that the decision is a pure
 * function: "did we already pay for this view" is exactly the kind of thing that quietly
 * stops working, so it is testable without a map.
 */

/** Below this the picture is useless and costs the same: an overview map of a field is a smudge. */
export const MIN_IMAGERY_ZOOM = 14;

/** Panning inside the area already paid for must not pay again. */
export const REFETCH_DISTANCE_KM = 30;

/** While the user is dragging the map there are no requests at all. */
export const DATES_DEBOUNCE_MS = 2000;

export interface Viewport {
  /** [west, south, east, north] — the same order the backend's bbox takes. */
  bbox: [number, number, number, number];
  center: [number, number];
  zoom: number;
}

const EARTH_RADIUS_KM = 6371;
const rad = (degrees: number): number => (degrees * Math.PI) / 180;

/** Great-circle distance. Flat-earth degrees would be wrong by 3x at 70 N (CLAUDE.md). */
export const haversineKm = (a: [number, number], b: [number, number]): number => {
  const [lonA, latA] = a;
  const [lonB, latB] = b;
  const dLat = rad(latB - latA);
  const dLon = rad(lonB - lonA);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h = sinLat * sinLat + Math.cos(rad(latA)) * Math.cos(rad(latB)) * sinLon * sinLon;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * The bbox as the API takes it. Six decimals, matching `canonical_bbox` on the server, so
 * the same view produces the same cache key on both sides instead of two near-identical ones.
 */
export const bboxParam = (bbox: [number, number, number, number]): string =>
  bbox.map((value) => value.toFixed(6)).join(",");

/**
 * THE TILE BOX — the second box, and the reason it exists.
 *
 * One bbox used to serve two jobs with different natural refresh rates: the DATES (metadata,
 * cheap, one request per 30 km) and the TILES (`bounds` on the raster layer, which decides
 * whether a tile is requested at all). A z14 viewport is ~11 km wide, so one screen of
 * panning left the tile `bounds` behind the view, `GridLayer._isValidTile` correctly
 * suppressed every tile, and the picture vanished with the toggle still on.
 *
 * Splitting them fixes it. The tile box follows the CURRENT view, but snapped to a grid
 * rather than equal to the screen: a screen-shaped box changes on every pan, which means a
 * new `getMapId` and a miss in both caches every time. A grid cell changes the key only when
 * the view crosses a cell edge, and coming back to where you were is free.
 *
 * The step is chosen so one cell is always WIDER than a z14 viewport across the whole region
 * (44-70 N): 1920 px at z14 is 0.165 deg of longitude, and less in latitude by cos(lat) —
 * 0.094 deg at 55 N, 0.056 deg at 70 N. So the union is at most 2x2 cells = 0.5 x 0.25 deg =
 * 0.125 deg2 against the server's MAX_BBOX_AREA_DEG2 = 5.0.
 */
export const TILE_BOX_STEP_LON_DEG = 0.25;
export const TILE_BOX_STEP_LAT_DEG = 0.125;

/** Past this the snapped box is not a saving any more — fall back to the raw view. */
export const TILE_BOX_MAX_AREA_DEG2 = 1.0;

export interface TileBox {
  /** [west, south, east, north], snapped outwards to the grid. */
  bbox: [number, number, number, number];
  /** The canonical string the tiles endpoint takes — and the cache key on both sides. */
  param: string;
  /** How many grid cells the union covers. Shown on the developer surface. */
  cells: number;
  areaDeg2: number;
  /** True when the grid was abandoned for the raw view (an unusually wide viewport). */
  fallback: boolean;
}

const snapDown = (value: number, step: number): number => Math.floor(value / step) * step;
const snapUp = (value: number, step: number): number => Math.ceil(value / step) * step;

/**
 * The box the raster layer is fetched and bounded by — or null when no picture is owed.
 *
 * `null` below zoom 14 keeps the cheapest guard of all inside the function that decides the
 * request, instead of next to it where it can be forgotten.
 */
export const tileBoxOf = (viewport: Viewport | null): TileBox | null => {
  if (!viewport || viewport.zoom < MIN_IMAGERY_ZOOM) return null;
  const [west, south, east, north] = viewport.bbox;
  const raw = (reason: boolean): TileBox => ({
    bbox: [west, south, east, north],
    param: bboxParam([west, south, east, north]),
    cells: 0,
    areaDeg2: Math.abs((east - west) * (north - south)),
    fallback: reason,
  });
  // A view that crosses the antimeridian (Chukotka does) or a degenerate one: snapping it
  // would invent a box the size of the planet, and that is exactly the bill this avoids.
  if (!(east > west) || !(north > south)) return raw(true);

  const bbox: [number, number, number, number] = [
    snapDown(west, TILE_BOX_STEP_LON_DEG),
    snapDown(south, TILE_BOX_STEP_LAT_DEG),
    snapUp(east, TILE_BOX_STEP_LON_DEG),
    snapUp(north, TILE_BOX_STEP_LAT_DEG),
  ];
  const areaDeg2 = (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]);
  if (areaDeg2 > TILE_BOX_MAX_AREA_DEG2) return raw(true);

  const cols = Math.round((bbox[2] - bbox[0]) / TILE_BOX_STEP_LON_DEG);
  const rows = Math.round((bbox[3] - bbox[1]) / TILE_BOX_STEP_LAT_DEG);
  return { bbox, param: bboxParam(bbox), cells: cols * rows, areaDeg2, fallback: false };
};

/** Just the key: what the tile request and its cache are addressed by. */
export const tileBbox = (viewport: Viewport | null): string | null =>
  tileBoxOf(viewport)?.param ?? null;

export type SkipReason = "zoom" | "near" | null;

/**
 * Whether the dates over this view have to be fetched, and if not, which guard said so.
 *
 * Returning the reason rather than a bare boolean is what lets the interface tell the user
 * "приблизьте карту" instead of showing an empty list and looking broken.
 */
export const datesFetchDecision = (
  next: Viewport,
  lastFetchedCenter: [number, number] | null,
): SkipReason => {
  if (next.zoom < MIN_IMAGERY_ZOOM) return "zoom";
  if (lastFetchedCenter && haversineKm(next.center, lastFetchedCenter) < REFETCH_DISTANCE_KM) {
    return "near";
  }
  return null;
};

export const shouldFetchDates = (
  next: Viewport,
  lastFetchedCenter: [number, number] | null,
): boolean => datesFetchDecision(next, lastFetchedCenter) === null;
